import React from 'react';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError.ts';
import { isUnauthenticatedError } from '@confluence/error-boundary/entry-points/isUnauthenticatedError.ts';

// Deliberately excludes generic network failures - those should still surface.
const EXPECTED_APPROVALS_DATA_ERROR_MESSAGES = [
	'PermissionException',
	'not listed in the IP allowlist',
	'Step-up authentication is required',
	'rate limit exceeded',
];

type ApprovalsGraphQLErrorLike = {
	message?: string;
	extensions?: Record<string, unknown>;
};

type ApprovalsDataErrorLike = ApprovalsGraphQLErrorLike & {
	graphQLErrors?: readonly ApprovalsGraphQLErrorLike[];
};

const matchesKnownExpectedMessage = (errorLike: ApprovalsGraphQLErrorLike): boolean =>
	EXPECTED_APPROVALS_DATA_ERROR_MESSAGES.some((message) => errorLike.message?.includes(message));

export function isExpectedApprovalsDataError(
	error: ApprovalsDataErrorLike | null | undefined,
): boolean {
	if (!error) {
		return false;
	}

	const isExpectedMessage = error.graphQLErrors?.length
		? error.graphQLErrors.every(matchesKnownExpectedMessage)
		: matchesKnownExpectedMessage(error);

	if (isExpectedMessage) {
		return true;
	}

	return isUnauthenticatedError(error as Error) || isTooManyRequestsError(error as Error);
}

type ApprovalsDataErrorDisplayProps = {
	error: Error;
};

export const ApprovalsDataErrorDisplay = ({
	error,
}: ApprovalsDataErrorDisplayProps): React.JSX.Element | null => {
	if (isExpectedApprovalsDataError(error)) {
		markErrorAsHandled(error);
		return null;
	}

	return <ErrorDisplay error={error} />;
};
